import { useState } from 'react';

interface Props {
  onClose: () => void;
  onGenerate: () => void;
  initialTitle?: string;
}

interface EbookFormData {
  title: string;
  genre: string;
  targetAudience: string;
  tone: string;
  purpose: string;
  chapters: string;
  keyTopics: string[];
  writingStyle: string;
  length: string;
  extraNotes: string;
}

const GENRES = [
  'Business & Entrepreneurship', 'Self-Help & Personal Development',
  'Technology & Programming', 'Health & Wellness', 'Finance & Investing',
  'Creative & Arts', 'Fiction & Storytelling', 'Marketing & Sales',
  'Career & Leadership', 'Education & Academic', 'Science & Nature',
  'Travel & Lifestyle', 'Cooking & Food', 'Sports & Fitness',
  'Parenting & Family', 'Spirituality & Mindfulness', 'Other',
];

const TONES = [
  { value: 'professional', label: 'Professional', icon: 'ri-briefcase-line', desc: 'Authoritative & credible' },
  { value: 'casual', label: 'Casual', icon: 'ri-chat-smile-line', desc: 'Friendly & relatable' },
  { value: 'academic', label: 'Academic', icon: 'ri-graduation-cap-line', desc: 'Scholarly & detailed' },
  { value: 'inspirational', label: 'Inspirational', icon: 'ri-sparkling-line', desc: 'Motivating & uplifting' },
  { value: 'storytelling', label: 'Storytelling', icon: 'ri-quill-pen-line', desc: 'Narrative & immersive' },
  { value: 'conversational', label: 'Conversational', icon: 'ri-message-line', desc: 'Direct & engaging' },
];

const PURPOSES = [
  { value: 'educate', label: 'Educate', icon: 'ri-book-open-line' },
  { value: 'inspire', label: 'Inspire', icon: 'ri-fire-line' },
  { value: 'entertain', label: 'Entertain', icon: 'ri-movie-line' },
  { value: 'sell', label: 'Lead Generation', icon: 'ri-store-line' },
  { value: 'course', label: 'Online Course', icon: 'ri-slideshow-line' },
  { value: 'memoir', label: 'Memoir / Story', icon: 'ri-user-heart-line' },
];

const TOPIC_SUGGESTIONS: Record<string, string[]> = {
  'Business & Entrepreneurship': ['Startup Strategy', 'Fundraising', 'Product-Market Fit', 'Team Building', 'Revenue Models', 'Go-to-Market'],
  'Self-Help & Personal Development': ['Morning Routines', 'Goal Setting', 'Habit Formation', 'Mindset Shifts', 'Time Management', 'Confidence'],
  'Technology & Programming': ['AI/ML Basics', 'Web Development', 'API Design', 'Cloud Architecture', 'DevOps', 'Data Structures'],
  'Health & Wellness': ['Nutrition Science', 'Exercise Physiology', 'Sleep Optimization', 'Stress Management', 'Mental Health', 'Recovery'],
  'Finance & Investing': ['Stock Market', 'Real Estate', 'Crypto', 'Budgeting', 'Retirement', 'Passive Income'],
  'Marketing & Sales': ['Content Marketing', 'SEO Strategy', 'Social Media', 'Email Marketing', 'Sales Funnels', 'Brand Building'],
};

const DEFAULT_TOPICS = ['Core Concepts', 'Practical Examples', 'Case Studies', 'Action Steps', 'Common Mistakes', 'Expert Insights', 'Tools & Resources', 'Q&A / FAQ'];

const CHAPTER_OPTIONS = ['5–7', '8–10', '11–14', '15–20', '20+'];
const LENGTH_OPTIONS = [
  { value: 'short', label: 'Short', pages: '50–80 pages', icon: 'ri-file-line' },
  { value: 'medium', label: 'Medium', pages: '80–150 pages', icon: 'ri-file-text-line' },
  { value: 'long', label: 'Long', pages: '150–300 pages', icon: 'ri-file-list-3-line' },
];

const STEPS = ['Topic & Genre', 'Content Setup', 'Style & Format', 'Review'];

const GENERATED_CHAPTERS = [
  { num: 1, title: 'Introduction & Core Foundation', pages: 14, type: 'intro' },
  { num: 2, title: 'The Essential Framework', pages: 18, type: 'theory' },
  { num: 3, title: 'Practical Strategies That Work', pages: 22, type: 'practical' },
  { num: 4, title: 'Deep Dive: Advanced Concepts', pages: 20, type: 'advanced' },
  { num: 5, title: 'Real-World Case Studies', pages: 24, type: 'case-study' },
  { num: 6, title: 'Common Mistakes & How to Avoid Them', pages: 16, type: 'warnings' },
  { num: 7, title: 'Tools & Resources', pages: 12, type: 'resources' },
  { num: 8, title: 'Your Step-by-Step Action Plan', pages: 18, type: 'action' },
  { num: 9, title: 'Measuring Success & Iterating', pages: 14, type: 'metrics' },
  { num: 10, title: 'Next Steps & Final Thoughts', pages: 10, type: 'conclusion' },
];

const CHAPTER_ICONS: Record<string, string> = {
  intro: 'ri-door-open-line',
  theory: 'ri-lightbulb-line',
  practical: 'ri-tools-line',
  advanced: 'ri-rocket-line',
  'case-study': 'ri-user-star-line',
  warnings: 'ri-error-warning-line',
  resources: 'ri-links-line',
  action: 'ri-list-check-2',
  metrics: 'ri-bar-chart-2-line',
  conclusion: 'ri-flag-line',
};

export default function GenerateCustomEbookModal({ onClose, onGenerate, initialTitle }: Props) {
  const [step, setStep] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);
  const [form, setForm] = useState<EbookFormData>({
    title: initialTitle || '', genre: '', targetAudience: '', tone: '',
    purpose: '', chapters: '8–10', keyTopics: [], writingStyle: '',
    length: 'medium', extraNotes: '',
  });

  const update = (key: keyof EbookFormData, val: string | string[]) =>
    setForm(prev => ({ ...prev, [key]: val }));

  const toggleTopic = (t: string) =>
    setForm(prev => ({
      ...prev,
      keyTopics: prev.keyTopics.includes(t) ? prev.keyTopics.filter(x => x !== t) : [...prev.keyTopics, t],
    }));

  const canNext = () => {
    if (step === 0) return form.title.trim().length > 0 && form.genre !== '' && form.targetAudience.trim().length > 0;
    if (step === 1) return form.purpose !== '' && form.keyTopics.length > 0;
    if (step === 2) return form.tone !== '' && form.length !== '';
    return true;
  };

  const handleGenerate = () => {
    setGenerating(true);
    setGenerationStep(0);
    const steps = [0, 1, 2, 3, 4];
    steps.forEach((_, i) => {
      setTimeout(() => {
        setGenerationStep(i + 1);
        if (i === steps.length - 1) {
          setTimeout(() => { setGenerating(false); setGenerated(true); }, 400);
        }
      }, (i + 1) * 580);
    });
  };

  const progressPct = ((step + 1) / STEPS.length) * 100;
  const suggestedTopics = TOPIC_SUGGESTIONS[form.genre] || DEFAULT_TOPICS;
  const allTopics = [...new Set([...suggestedTopics, ...DEFAULT_TOPICS])].slice(0, 12);

  const generatingSteps = [
    'Analyzing genre & target audience',
    'Building chapter structure',
    'Crafting section outlines',
    'Adding expert frameworks',
    'Finalizing your eBook outline',
  ];

  const chapterCount = parseInt(form.chapters?.split('–')[0] || '8');
  const finalChapters = GENERATED_CHAPTERS.slice(0, Math.min(chapterCount, GENERATED_CHAPTERS.length));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">

        {/* ── Header ── */}
        <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 px-8 py-6 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <i className="ri-magic-line text-white text-xl"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Generate Custom eBook</h2>
                <p className="text-white/75 text-xs">AI builds your personalized eBook outline in seconds</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors cursor-pointer">
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
          {!generated && !generating && (
            <>
              <div className="flex items-center gap-1 mb-3">
                {STEPS.map((s, i) => (
                  <div key={s} className="flex items-center gap-1 flex-1">
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${i < step ? 'bg-white text-orange-600 border-white' : i === step ? 'bg-white/20 border-white text-white' : 'bg-transparent border-white/30 text-white/40'}`}>
                        {i < step ? <i className="ri-check-line text-xs"></i> : i + 1}
                      </div>
                      <span className={`text-xs font-medium hidden sm:block ${i <= step ? 'text-white' : 'text-white/40'}`}>{s}</span>
                    </div>
                    {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 rounded-full mx-1 ${i < step ? 'bg-white' : 'bg-white/25'}`} />}
                  </div>
                ))}
              </div>
              <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
              </div>
            </>
          )}
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto p-8">

          {/* Generating Animation */}
          {generating && (
            <div className="flex flex-col items-center justify-center py-14 gap-6">
              <div className="relative w-20 h-20">
                <div className="absolute inset-0 rounded-full border-4 border-amber-100"></div>
                <div className="absolute inset-0 rounded-full border-4 border-amber-500 border-t-transparent animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <i className="ri-book-2-line text-amber-500 text-2xl"></i>
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-bold text-slate-900 mb-1">AI is crafting your eBook…</h3>
                <p className="text-sm text-slate-500">Building a personalized outline based on your inputs</p>
              </div>
              <div className="w-full max-w-xs space-y-2.5">
                {generatingSteps.map((label, i) => (
                  <div key={label} className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      {generationStep > i
                        ? <i className="ri-checkbox-circle-fill text-emerald-500 text-lg"></i>
                        : generationStep === i
                        ? <div className="w-4 h-4 rounded-full border-2 border-amber-400 border-t-transparent animate-spin"></div>
                        : <div className="w-3 h-3 rounded-full bg-slate-200"></div>
                      }
                    </div>
                    <span className={`text-sm transition-colors ${generationStep > i ? 'text-emerald-600 font-medium' : generationStep === i ? 'text-slate-800 font-semibold' : 'text-slate-400'}`}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Generated Result */}
          {generated && (
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <i className="ri-check-double-line text-emerald-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Your eBook Outline is Ready!</h3>
                  <p className="text-xs text-slate-500">
                    AI-generated for <strong className="text-slate-700">&ldquo;{form.title}&rdquo;</strong> · {form.genre}
                  </p>
                </div>
              </div>

              {/* eBook meta */}
              <div className="grid grid-cols-4 gap-3 mb-5">
                {[
                  { icon: 'ri-book-open-line', label: 'Chapters', val: finalChapters.length },
                  { icon: 'ri-pages-line', label: 'Est. Pages', val: LENGTH_OPTIONS.find(l => l.value === form.length)?.pages.split('–')[0] + '+' },
                  { icon: 'ri-time-line', label: 'Read Time', val: `${Math.ceil(finalChapters.length * 1.5)}h` },
                  { icon: 'ri-star-fill', label: 'Complexity', val: form.tone === 'academic' ? 'Advanced' : 'Medium' },
                ].map(stat => (
                  <div key={stat.label} className="text-center p-3 bg-amber-50 rounded-xl">
                    <div className="w-7 h-7 flex items-center justify-center mx-auto mb-1 text-amber-600">
                      <i className={`${stat.icon} text-base`}></i>
                    </div>
                    <div className="text-base font-extrabold text-slate-900">{stat.val}</div>
                    <div className="text-xs text-slate-500">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Chapter outline */}
              <div className="space-y-2 mb-5">
                {finalChapters.map((ch) => (
                  <div key={ch.num} className="flex items-start gap-3 p-3.5 bg-slate-50 rounded-xl border border-slate-100 hover:border-amber-200 hover:bg-amber-50/30 transition-colors group">
                    <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-amber-200 transition-colors">
                      <i className={`${CHAPTER_ICONS[ch.type] || 'ri-file-line'} text-amber-600 text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-400">Ch. {String(ch.num).padStart(2, '0')}</span>
                        <h4 className="text-sm font-semibold text-slate-800 truncate">{ch.title}</h4>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">~{ch.pages} pages · AI-generated content</p>
                    </div>
                    <i className="ri-check-line text-emerald-500 flex-shrink-0 mt-0.5"></i>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <i className="ri-information-line text-amber-500 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-xs text-amber-700 leading-relaxed">
                  Sign in to unlock the full AI writing experience — generate complete chapter content, add your own text, upload images, and export to PDF or EPUB.
                </p>
              </div>
            </div>
          )}

          {/* Step 0 — Topic & Genre */}
          {!generating && !generated && step === 0 && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-2">eBook Title or Topic <span className="text-rose-500">*</span></label>
                <input
                  type="text"
                  placeholder="e.g. The Complete Guide to Personal Finance, Python for Beginners…"
                  value={form.title}
                  onChange={e => update('title', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent placeholder-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-2">Genre <span className="text-rose-500">*</span></label>
                <select
                  value={form.genre}
                  onChange={e => { update('genre', e.target.value); update('keyTopics', []); }}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white text-slate-800 cursor-pointer"
                >
                  <option value="">Select a genre…</option>
                  {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-2">Target Audience <span className="text-rose-500">*</span></label>
                <input
                  type="text"
                  placeholder="e.g. First-time entrepreneurs, college students, busy parents…"
                  value={form.targetAudience}
                  onChange={e => update('targetAudience', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent placeholder-slate-400"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-2">Extra Notes <span className="text-slate-400 font-normal">(optional)</span></label>
                <textarea
                  placeholder="Any specific angles, stories, or requirements you want included?"
                  value={form.extraNotes}
                  onChange={e => update('extraNotes', e.target.value)}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 placeholder-slate-400 resize-none"
                />
                <p className="text-xs text-slate-400 mt-1 text-right">{form.extraNotes.length}/500</p>
              </div>
            </div>
          )}

          {/* Step 1 — Content Setup */}
          {!generating && !generated && step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-3">eBook Purpose <span className="text-rose-500">*</span></label>
                <div className="grid grid-cols-3 gap-3">
                  {PURPOSES.map(p => (
                    <button
                      key={p.value}
                      onClick={() => update('purpose', p.value)}
                      className={`p-3 rounded-xl border-2 text-center transition-all cursor-pointer ${form.purpose === p.value ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-amber-300'}`}
                    >
                      <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                        <i className={`${p.icon} text-xl ${form.purpose === p.value ? 'text-amber-600' : 'text-slate-400'}`}></i>
                      </div>
                      <span className={`text-xs font-bold ${form.purpose === p.value ? 'text-amber-700' : 'text-slate-700'}`}>{p.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-1">
                  Key Topics to Cover <span className="text-rose-500">*</span>
                  <span className="text-slate-400 font-normal ml-1">(select at least one)</span>
                </label>
                <p className="text-xs text-slate-400 mb-3">
                  {form.genre ? `Suggested for ${form.genre}` : 'Common topics'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {allTopics.map(topic => (
                    <button
                      key={topic}
                      onClick={() => toggleTopic(topic)}
                      className={`px-3.5 py-1.5 rounded-full text-sm font-medium border-2 transition-all cursor-pointer whitespace-nowrap ${form.keyTopics.includes(topic) ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600 hover:border-amber-300'}`}
                    >
                      {form.keyTopics.includes(topic) && <i className="ri-check-line mr-1 text-xs"></i>}
                      {topic}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-3">Number of Chapters</label>
                <div className="flex gap-2 flex-wrap">
                  {CHAPTER_OPTIONS.map(opt => (
                    <button
                      key={opt}
                      onClick={() => update('chapters', opt)}
                      className={`px-5 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${form.chapters === opt ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600 hover:border-amber-300'}`}
                    >
                      {opt} chapters
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2 — Style & Format */}
          {!generating && !generated && step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-3">Writing Tone <span className="text-rose-500">*</span></label>
                <div className="grid grid-cols-2 gap-3">
                  {TONES.map(t => (
                    <button
                      key={t.value}
                      onClick={() => update('tone', t.value)}
                      className={`p-3.5 rounded-xl border-2 text-left transition-all cursor-pointer flex items-start gap-3 ${form.tone === t.value ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-amber-300'}`}
                    >
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${form.tone === t.value ? 'bg-amber-100' : 'bg-slate-100'}`}>
                        <i className={`${t.icon} text-lg ${form.tone === t.value ? 'text-amber-600' : 'text-slate-500'}`}></i>
                      </div>
                      <div>
                        <p className={`text-sm font-bold ${form.tone === t.value ? 'text-amber-700' : 'text-slate-800'}`}>{t.label}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{t.desc}</p>
                      </div>
                      {form.tone === t.value && <i className="ri-checkbox-circle-fill text-amber-500 ml-auto flex-shrink-0 text-lg mt-0.5"></i>}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-3">eBook Length <span className="text-rose-500">*</span></label>
                <div className="grid grid-cols-3 gap-3">
                  {LENGTH_OPTIONS.map(l => (
                    <button
                      key={l.value}
                      onClick={() => update('length', l.value)}
                      className={`p-4 rounded-xl border-2 text-center transition-all cursor-pointer ${form.length === l.value ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-amber-300'}`}
                    >
                      <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                        <i className={`${l.icon} text-2xl ${form.length === l.value ? 'text-amber-600' : 'text-slate-400'}`}></i>
                      </div>
                      <p className={`text-sm font-bold ${form.length === l.value ? 'text-amber-700' : 'text-slate-700'}`}>{l.label}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{l.pages}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3 — Review */}
          {!generating && !generated && step === 3 && (
            <div className="space-y-5">
              <p className="text-sm text-slate-500">Review your inputs before AI generates your eBook outline.</p>
              <div className="bg-slate-50 rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                {[
                  { label: 'Title / Topic', value: form.title },
                  { label: 'Genre', value: form.genre },
                  { label: 'Target Audience', value: form.targetAudience },
                  { label: 'Purpose', value: PURPOSES.find(p => p.value === form.purpose)?.label || '—' },
                  { label: 'Key Topics', value: form.keyTopics.slice(0, 4).join(', ') + (form.keyTopics.length > 4 ? ` +${form.keyTopics.length - 4} more` : '') || '—' },
                  { label: 'Chapters', value: form.chapters },
                  { label: 'Writing Tone', value: TONES.find(t => t.value === form.tone)?.label || '—' },
                  { label: 'Length', value: LENGTH_OPTIONS.find(l => l.value === form.length)?.pages || '—' },
                ].map(row => (
                  <div key={row.label} className="flex items-start gap-4 px-5 py-3">
                    <span className="text-xs font-semibold text-slate-400 w-32 flex-shrink-0 pt-0.5">{row.label}</span>
                    <span className="text-sm text-slate-800 font-medium">{row.value}</span>
                  </div>
                ))}
              </div>
              {form.extraNotes && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-xs font-semibold text-amber-600 mb-1">Extra Notes</p>
                  <p className="text-sm text-slate-700">{form.extraNotes}</p>
                </div>
              )}
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <i className="ri-sparkling-line text-amber-500 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-xs text-slate-600 leading-relaxed">
                  OpenClaw AI will generate a complete chapter-by-chapter outline, estimated page counts, and writing guidelines tailored to your genre, tone, and audience.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-8 py-5 border-t border-slate-100 flex items-center justify-between flex-shrink-0 bg-white">
          {generated ? (
            <>
              <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap">
                Close
              </button>
              <button
                onClick={onGenerate}
                className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-bold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap flex items-center gap-2 shadow-lg"
              >
                <i className="ri-edit-line"></i>
                Start Writing Full eBook
              </button>
            </>
          ) : generating ? (
            <div className="w-full flex justify-center">
              <span className="text-sm text-slate-400">Generating your outline…</span>
            </div>
          ) : (
            <>
              <button
                onClick={() => step === 0 ? onClose() : setStep(s => s - 1)}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {step === 0 ? 'Cancel' : '← Back'}
              </button>
              {step < 3 ? (
                <button
                  onClick={() => setStep(s => s + 1)}
                  disabled={!canNext()}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-bold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap"
                >
                  Continue →
                </button>
              ) : (
                <button
                  onClick={handleGenerate}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-bold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap flex items-center gap-2 shadow-lg"
                >
                  <i className="ri-magic-line"></i>
                  Generate My eBook
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
